import {EventEmitter, Injectable} from '@angular/core';
import {TicketCreate} from '../models/ticket.model';
import {Subject} from 'rxjs';
import {Discount} from '../models/discount.model';
import {NgbDateParserFormatter} from '@ng-bootstrap/ng-bootstrap';
import {RouteGroup} from "../models/routeGroup.model";
import {takeUntil} from "rxjs/operators";

@Injectable({providedIn: 'root'})
export class BookingService {

    adult_num: number;
    adult_num_changed = new Subject<number>();
    child_num: number;
    child_num_changed = new Subject<number>();
    infant_num: number;
    infant_num_changed = new Subject<number>();

    dep_adult_price = 0;
    dep_child_price = 0;
    dep_infant_price = 0;
    re_adult_price = 0;
    re_child_price = 0;
    re_infant_price = 0;

    ticketDep: TicketCreate;
    ticketRe: TicketCreate;

    is_round_trip: boolean;

    private passengerAdultsValue: any;
    private passengerChildrenValue: any;
    private passengerInfantsValue: any;

    private routeIdDep: number;
    private dateDep: string;
    private routeIdRe: number;
    private dateRe: string;

    is_valid_ticket: boolean;
    isValidTicketChanged = new Subject<boolean>();

    invoiceAmount: number;
    invoiceAmountChanged = new Subject<number>();

    setTicketsEmitter = new EventEmitter<number>();
    submitFirst = new Subject<boolean>();

    routeGroups: RouteGroup[];
    private discounts: Discount[];
    private discountInUse: Discount;
    discountInUseSub = new Subject<Discount>();

    pnr: string;

    constructor(private ngDateParserFormatter: NgbDateParserFormatter) {
    }

    setIsValidTicket(isValid) {
        this.is_valid_ticket = isValid;
        this.isValidTicketChanged.next(isValid);
    }

    setPassengerAdultsValue(passengerAdultsValue) {
        this.passengerAdultsValue = passengerAdultsValue;
    }

    getPassengerAdultsValue() {
        return this.passengerAdultsValue;
    }

    setPassengerChildrenValue(passengerChildrenValue) {
        this.passengerChildrenValue = passengerChildrenValue;
    }

    getPassengerChildrenValue() {
        return this.passengerChildrenValue;
    }

    setPassengerInfantsValue(passengerInfantsValue) {
        this.passengerInfantsValue = passengerInfantsValue;
    }

    getPassengerInfantsValue() {
        return this.passengerInfantsValue;
    }

    setDepTicket(route_id: number, date: any, is_blocked: boolean) {
        date = date.year + '-' + date.month + '-' + date.day;
        this.routeIdDep = route_id;
        this.dateDep = date;
        const status = is_blocked ? 'blocked' : 'confirmed';
        this.ticketDep = new TicketCreate(this.dateDep, this.routeIdDep, null, status);
    }

    getDepTicket() {
        return this.ticketDep;
    }

    setReTicket(route_id: number, date: any, is_blocked: boolean) {
        date = date.year + '-' + date.month + '-' + date.day;
        this.routeIdRe = route_id;
        this.dateRe = date;
        const status = is_blocked ? 'blocked' : 'confirmed';
        this.ticketRe = new TicketCreate(this.dateRe, this.routeIdRe, null, status);
    }

    getReTicket() {
        return this.ticketRe;
    }

    setIsRoundTrip(is_round_trip) {
        this.is_round_trip = is_round_trip;
    }

    getIsRoundTrip() {
        return this.is_round_trip;
    }

    setPnr(pnr: string) {
        this.pnr = pnr;
    }

    getPnr() {
        return this.pnr;
    }


    // *** Discount *** //

    setDiscount(discounts: Discount[]) {
        this.discounts = discounts;
    }

    // have to add calculate discount amount and show
    checkIsValidDiscountCode(code: string, dateRaw: any) {
        const date = typeof (dateRaw) === 'string' ? dateRaw : this.ngDateParserFormatter.format(dateRaw);
        const discount_code_obj: Discount = this.discounts.filter(f => f.code === code)[0];
        // check if discount code exist
        if (!discount_code_obj) {
            return false;
        }
        // check if traveling date expired
        if (!(discount_code_obj.start <= date && discount_code_obj.end >= date)) {
            return false;
        }
        this.setDiscountInUse(discount_code_obj);
        return true;
    }

    getDiscountAmountById(discount_id: number) {
        for (const discount of this.discounts) {
            if (discount.id === discount_id) {
                return discount.amount;
            }
        }
    }

    getDiscountAmountByCode(code: string) {
        for (const discount of this.discounts) {
            if (discount.code === code) {
                return discount.amount;
            }
        }
    }

    setDiscountInUse(discount: Discount) {
        this.discountInUse = discount;
        this.discountInUseSub.next(discount);
    }

    resetDiscountInUse() {
        this.discountInUse = null;
    }

    setDiscountInUseById(discount_id: number) {
        this.discountInUse = this.discounts.filter(item => item.id === discount_id)[0];
        this.discountInUseSub.next(this.discountInUse);
    }

    getDiscountInUse() {
        return this.discountInUse;
    }


    // *** Price *** //

    setPrice(dep_adult_price: number, dep_child_price: number,  dep_infant_price: number
        , re_adult_price: number, re_child_price: number, re_infant_price: number) {
        this.dep_adult_price = dep_adult_price;
        this.dep_child_price = dep_child_price;
        this.dep_infant_price = dep_infant_price;
        this.re_adult_price = re_adult_price;
        this.re_child_price = re_child_price;
        this.re_infant_price = re_infant_price;

        this.setInvoice();
    }

    setInvoice() {
        const dep_adult_price = this.dep_adult_price;
        const dep_child_price = this.dep_child_price;
        const dep_infant_price = this.dep_infant_price;
        const re_adult_price = this.re_adult_price !== 0 ? this.re_adult_price : null;
        const re_child_price = this.re_child_price !== 0 ? this.re_child_price : null;
        const re_infant_price = this.re_infant_price !== 0 ? this.re_infant_price : null;
        if (this.discountInUse) {
            // promo_code discount
            this.invoiceAmount = ((dep_adult_price - +this.discountInUse.amount) * this.adult_num)
                + ((dep_child_price - +this.discountInUse.amount) * this.child_num)
                + ((dep_infant_price - +this.discountInUse.amount) * this.infant_num)
                + ((re_adult_price ? re_adult_price - +this.discountInUse.amount : 0) * this.adult_num)
                + ((re_child_price ? re_child_price - +this.discountInUse.amount : 0) * this.child_num)
                + ((re_infant_price ? re_infant_price - +this.discountInUse.amount : 0) * this.infant_num);
        } else {
            // valid_ticket discount
  /*          if (this.is_valid_ticket) {
                this.invoiceAmount = 0;
            } else {*/
                this.invoiceAmount = (dep_adult_price * this.adult_num)
                    + (dep_child_price * this.child_num)
                    + (dep_infant_price * this.infant_num)
                    + (re_adult_price ? re_adult_price * this.adult_num : 0)
                    + (re_child_price ? re_child_price * this.child_num : 0)
                    + (re_infant_price ? re_infant_price * this.infant_num : 0);
        }
        this.invoiceAmountChanged.next(this.invoiceAmount);
    }


    setAdultNum(num: number) {
        this.adult_num = num;
    }

    getAdultNum() {
        return this.adult_num;
    }

    setChildNum(num: number) {
        this.child_num = num;
    }

    getChildNum() {
        return this.child_num;
    }

    setInfantNum(num: number) {
        this.infant_num = num;
    }

    getInfantNum() {
    return this.infant_num;
    }

}
